/* Les écritures d'une usine.
 *
 * Aucune ne passe par une table. Toutes appellent une fonction de la base qui
 * nomme les colonnes permises, une par une — parce qu'une politique RLS
 * contrôle la ligne produite, pas les colonnes touchées : autorisée à modifier
 * sa propre ligne, une usine se remettrait au catalogue ou changerait notre
 * prix de vente dans le même mouvement.
 *
 * La convention de ces fonctions : un champ absent reste en place, une chaîne
 * vide l'efface. On n'envoie donc que ce qui a changé.
 */

import { supabase } from "@/lib/supabase";
import type { ProduitUsine, Usine } from "@/types/metier";

function echoue(erreur: { message: string } | null): void {
  if (erreur) throw new Error(erreur.message);
}

/** Les colonnes de sa fiche qu'une usine a le droit de poser. */
const IDENTITE = [
  "adresse", "code_postal", "ville", "pays", "telephone", "email", "site",
  "langue", "registre", "tva", "incoterm", "conditions_paiement",
  "delai_production", "presentation",
] as const;

export async function majIdentite(champs: Partial<Usine>): Promise<void> {
  const params: Record<string, unknown> = {};
  for (const c of IDENTITE) {
    if (!(c in champs)) continue;
    const v = (champs as Record<string, unknown>)[c];
    params[`p_${c}`] = v == null ? "" : String(v);
  }
  if (!Object.keys(params).length) return;
  const { error } = await supabase.rpc("usine_maj_identite", params);
  echoue(error);
}

/** Les caractéristiques d'une référence. Ni le prix, ni la désignation. */
const TECHNIQUE = [
  "conditionnement", "code_douane", "origine", "poids_net", "poids_brut", "unite",
  "par_carton", "par_palette", "dluo", "allergenes", "ingredients", "ean",
] as const;

const NOMBRES = new Set(["poids_net", "poids_brut", "par_carton", "par_palette"]);

export async function majProduit(id: string, champs: Partial<ProduitUsine>): Promise<void> {
  const params: Record<string, unknown> = { p_produit: id };
  for (const c of TECHNIQUE) {
    if (!(c in champs)) continue;
    const v = (champs as Record<string, unknown>)[c];
    /* Un nombre vidé ne s'envoie pas : la base le lirait comme « inchangé »,
       et c'est le bon comportement — on ne vide pas un poids par mégarde. */
    if (NOMBRES.has(c)) {
      if (v != null && v !== "") params[`p_${c}`] = Number(v);
    } else {
      params[`p_${c}`] = v == null ? "" : String(v);
    }
  }
  if (Object.keys(params).length === 1) return;
  const { error } = await supabase.rpc("usine_maj_produit", params);
  echoue(error);
}

/** Annoncer un prix. Il ne court pas : il attend que la direction tranche. */
export async function proposerPrix(
  produitId: string,
  prix: number,
  applicable: string | null,
  motif: string | null,
): Promise<void> {
  const { error } = await supabase.rpc("usine_proposer_prix", {
    p_produit: produitId,
    p_prix: prix,
    p_applicable: applicable || null,
    p_motif: motif || null,
  });
  echoue(error);
}

/* ───────────────────────────── côté Omnix ────────────────────────────────── */

export async function trancherProposition(
  id: string, accepter: boolean, reponse: string | null,
): Promise<void> {
  const { error } = await supabase.rpc("trancher_proposition", {
    p_proposition: id, p_accepter: accepter, p_reponse: reponse || null,
  });
  echoue(error);
}

/** Ouvre une fiche d'accès pour une usine. L'invitation part ensuite. */
export async function creerAccesUsine(
  usineId: string, nom: string, email: string,
): Promise<string> {
  const { data, error } = await supabase.rpc("creer_acces_usine", {
    p_usine: usineId, p_nom: nom.trim(), p_email: email.trim().toLowerCase(),
  });
  echoue(error);
  return data as string;
}

/** Chacun corrige ses propres coordonnées — son niveau reste hors d'atteinte. */
export async function majMesCoordonnees(nom: string, telephone: string | null): Promise<void> {
  const { error } = await supabase.rpc("maj_mes_coordonnees", {
    p_nom: nom, p_telephone: telephone || null,
  });
  echoue(error);
}
